import { endpoints } from "../../config/apiEndpoint";
import { apiClient } from "../shared/apiClient";

/**
 * get categories.
 *
 * @return {Promise} A promise for getCategories.
 */
export async function getCategories() {
  try {
    const response = await apiClient.get(
      endpoints.products.categories,
      {},
      false
    );
    return response;
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }
}

/**
 * Get products by category with pagination.
 *
 * @param {number} page - Current page number.
 * @param {number} limit - Number of items per page.
 * @param {number} categoryId - Category ID to filter products.
 * @return {Promise} A promise for products list.
 */
export async function fetchProducts({
  page = 1,
  limit = 10,
  categoryId,
  locationId
}) {
  let query = `?page=${page}&limit=${limit}`;

  if (categoryId !== undefined) {
    query += `&categoryId=${categoryId}`;
  }

  if (locationId !== undefined) {
    query += `&locationIds=${locationId}`;
  }

  return apiClient.get(`${endpoints.products.list}${query}`, {}, false);
}

/**
 * add to cart
 *
 * @param {object} cart - The object item.
 * @return {Promise} A promise that resolves with products.
 */
export async function addToCart({ productVariantId, quantity }) {
  try {
    const response = await apiClient.post(
      endpoints.cart.add,
      {
        productVariantId,
        quantity
      },
      true
    );
    return response;
  } catch (error) {
    console.error("Error adding to cart:", error);
    throw error;
  }
}

/**
 * bulk add to cart
 *
 * @param {Array} items - The array of items.
 * @return {Promise} A promise that resolves with products.
 */
export async function bulkUpdateCart(items) {
  try {
    const response = await apiClient.post(
      endpoints.cart.bulkAdd,
      { items },
      true
    );
    return response;
  } catch (error) {
    console.error("Error bulk adding to cart:", error);
    throw error;
  }
}

/**
 * fetch cart
 *
 * @return {Promise} A promise that resolves with cart items.
 */
export async function fetchCart() {
  try {
    const response = await apiClient.get(endpoints.cart.list, {}, true);
    return response;
  } catch (error) {
    console.error("Error fetching cart details:", error);
    throw error;
  }
}

/**
 * increment cart item quantity
 *
 * @param {number} bundleId - The bundle ID to increment.
 * @param {number} incrementBy - Amount to increment by (default: 1).
 * @return {Promise} A promise that resolves with updated cart.
 */
export async function incrementCartQty({
  bundleId,
  productVariantId,
  incrementBy = 1
}) {
  try {
    const payload = { incrementBy };

    if (bundleId !== undefined) {
      payload.bundleId = bundleId;
    } else if (productVariantId !== undefined) {
      payload.productVariantId = productVariantId;
    }

    const response = await apiClient.patch(
      endpoints.cart.increment,
      payload,
      true
    );
    return response;
  } catch (error) {
    console.error("Error incrementing cart quantity:", error);
    throw error;
  }
}

/**
 * decrement cart item quantity
 *
 * @param {number} bundleId - The bundle ID to decrement.
 * @param {number} decrementBy - Amount to decrement by (default: 1).
 * @return {Promise} A promise that resolves with updated cart.
 */
export async function decrementCartQty({
  bundleId,
  productVariantId,
  decrementBy = 1
}) {
  try {
    const payload = { decrementBy };

    if (bundleId !== undefined) {
      payload.bundleId = bundleId;
    } else if (productVariantId !== undefined) {
      payload.productVariantId = productVariantId;
    }

    const response = await apiClient.patch(
      endpoints.cart.decrement,
      payload,
      true
    );
    return response;
  } catch (error) {
    console.error("Error decrementing cart quantity:", error);
    throw error;
  }
}

/**
 * clear entire cart
 *
 * @return {Promise} A promise that resolves when cart is cleared.
 */
export async function clearCart() {
  try {
    const response = await apiClient.delete(endpoints.cart.clear, {}, true);
    return response;
  } catch (error) {
    console.error("Error clearing cart:", error);
    throw error;
  }
}
